'use client';

import { useState, useMemo, useRef, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { ValidatedInput } from '@/components/forms/validated-input';
import { ActionButton } from '@/components/buttons/action-button';
import { useAuth } from '@/providers/auth-provider';
import { showFormError } from '@/lib/form-error';

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AuthForm() {
  const { isAuthenticated, isLoading, authStep, login, verify, pendingEmail, resetAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Validation
  const isEmailValid = useMemo(() => emailRegex.test(email), [email]);
  const isPasswordValid = useMemo(() => password.length >= 6, [password]);
  const isCodeValid = useMemo(() => code.length === 5, [code]);

  const isLoginFormValid = isEmailValid && isPasswordValid;

  // Focus on email input when form is shown
  useEffect(() => {
    if (authStep === 'login' && emailInputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 100);
    }
  }, [authStep]);

  // Focus on code input when verification step is shown
  useEffect(() => {
    if (authStep === 'verify' && codeInputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        codeInputRef.current?.focus();
      }, 100);
    }
  }, [authStep]);

  // Don't show if authenticated or still loading
  if (isAuthenticated || isLoading) {
    return null;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoginFormValid) return;
    
    setError('');
    setSubmitting(true);

    try {
      await login(email, password);
    } catch (err: unknown) {
      showFormError(err instanceof Error ? err.message : 'Ошибка входа', emailInputRef);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isCodeValid) return;
    
    setError('');
    setSubmitting(true);

    try {
      await verify(code);
    } catch (err: unknown) {
      showFormError(err instanceof Error ? err.message : 'Неверный код', codeInputRef);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setCode('');
    setError('');
    resetAuth();
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center px-4 py-8 bg-white">
      <div className="w-full max-w-md">
        <div className="rounded-lg border border-[#E5E5E5] bg-white p-6 shadow-sm sm:p-8">
          {authStep === 'login' ? (
            <>
              <div className="mb-6 text-center">
                <div className="mb-4 flex justify-center">
                  <img src="/logo.svg" alt="Giglan" className="h-8 w-auto" />
                </div>
                <h1 className="text-2xl font-bold text-[#1A1A1A]">
                  Вход в систему
                </h1>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <ValidatedInput
                  ref={emailInputRef}
                  id="auth_email"
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="tech@giglan.pro"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  isValid={email.length > 0 && isEmailValid}
                  autoComplete="email"
                  autoFocus
                />

                <ValidatedInput
                  id="auth_password"
                  name="password"
                  label="Пароль"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  isValid={password.length > 0 && isPasswordValid}
                  autoComplete="current-password"
                />

                <ActionButton
                  type="submit"
                  className="w-full"
                  loading={submitting}
                  disabled={!isLoginFormValid || submitting}
                >
                  Войти
                </ActionButton>
              </form>
            </>
          ) : (
            <>
              <div className="mb-6">
                <button
                  onClick={handleBack}
                  className="mb-4 flex items-center gap-1 text-sm font-medium text-[#68707D] hover:text-[#1A1A1A] transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Назад
                </button>
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-[#1A1A1A]">
                    Подтверждение
                  </h1>
                  <p className="mt-2 text-sm text-[#666666]">
                    Введите код из письма, отправленного на {pendingEmail}
                  </p>
                </div>
              </div>

              <form onSubmit={handleVerify} className="space-y-4">
                <ValidatedInput
                  ref={codeInputRef}
                  id="auth_verify_code"
                  name="verify_code"
                  label="Код подтверждения"
                  type="text"
                  inputMode="numeric"
                  placeholder="12345"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  isValid={isCodeValid}
                  className="text-center text-2xl tracking-[0.5em]"
                  maxLength={5}
                />

                <ActionButton
                  type="submit"
                  className="w-full"
                  loading={submitting}
                  disabled={!isCodeValid || submitting}
                >
                  Подтвердить
                </ActionButton>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}



